import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import './DateFormatDialog.css';

const PRESETS = [
    { label: 'DD.MM.YYYY', value: 'DD.MM.YYYY' },
    { label: 'MM.DD.YYYY', value: 'MM.DD.YYYY' },
    { label: 'YYYY-MM-DD', value: 'YYYY-MM-DD' },
    { label: 'Custom...', value: '__custom__' },
];

const DEFAULT_POS = { top: 160, left: window.innerWidth / 2 - 160 };

/**
 * Dialog for editing the date format setting.
 * Supports presets and free-text C# style format strings via moment.js.
 * @param {object} props
 * @param {boolean} props.open
 * @param {string} props.currentValue - Current format string
 * @param {function} props.onSave - fn(formatString)
 * @param {function} props.onClose
 */
export default function DateFormatDialog({ open, currentValue, onSave, onClose }) {
    const isCustom = (val) => !PRESETS.slice(0, -1).some((p) => p.value === val);

    const [selected, setSelected] = useState(PRESETS[0].value);
    const [custom, setCustom] = useState('');
    const [error, setError] = useState(false);
    const [pos, setPos] = useState(DEFAULT_POS);
    const [dragging, setDragging] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });

    useEffect(() => {
        if (!open) return;
        setPos(DEFAULT_POS);
        if (isCustom(currentValue)) {
            setSelected('__custom__');
            setCustom(currentValue);
        } else {
            setSelected(currentValue);
            setCustom('');
        }
        setError(false);
    }, [open]);

    const activeFormat = selected === '__custom__' ? custom : selected;
    const preview = activeFormat ? moment().format(activeFormat) : '';
    const isValid = activeFormat.trim() !== '' && preview !== activeFormat; // moment returns input unchanged if invalid
    const canSave = isValid;

    const handlePresetChange = (val) => {
        setSelected(val);
        setError(false);
    };

    const handleCustomChange = (val) => {
        setCustom(val);
        setError(false);
    };

    const handleSave = () => {
        if (!canSave) { setError(true); return; }
        onSave(activeFormat);
        onClose();
    };

    const handleDragStart = (e) => {
        dragOffset.current = { x: e.clientX - pos.left, y: e.clientY - pos.top };
        setDragging(true);
    };
    const handleDragMove = (e) => {
        if (!dragging) return;
        setPos({ left: e.clientX - dragOffset.current.x, top: e.clientY - dragOffset.current.y });
    };
    const handleDragEnd = () => setDragging(false);

    if (!open) return null;

    return createPortal(
        <div
            className="dfd-dialog"
            style={{ top: pos.top, left: pos.left }}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
        >
            {/* Titlebar */}
            <div className="dfd-titlebar" onMouseDown={handleDragStart} style={{ cursor: dragging ? 'grabbing' : 'grab' }}>
                <CalendarMonthOutlinedIcon sx={{ fontSize: 13, color: '#a3d1ff', mr: '6px' }} />
                <span>Date Format</span>
                <div className="dfd-titlebar-actions">
                    <CheckIcon
                        sx={{ fontSize: 16, cursor: canSave ? 'pointer' : 'default', color: canSave ? '#4caf8a' : '#294766' }}
                        onClick={handleSave}
                    />
                    <CloseIcon sx={{ fontSize: 16, cursor: 'pointer', color: '#a3d1ff' }} onClick={onClose} />
                </div>
            </div>

            <div className="dfd-body">
                {/* Preset dropdown */}
                <div className="dfd-row">
                    <span className="dfd-label">Preset</span>
                    <select className="dfd-select" value={selected} onChange={(e) => handlePresetChange(e.target.value)}>
                        {PRESETS.map((p) => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                    </select>
                </div>

                {/* Custom input — only when Custom selected */}
                {selected === '__custom__' && (
                    <div className="dfd-row">
                        <span className="dfd-label">Format string</span>
                        <input
                            className={`dfd-input${error ? ' dfd-input--error' : ''}`}
                            type="text"
                            value={custom}
                            onChange={(e) => handleCustomChange(e.target.value)}
                            placeholder="e.g. YYYY-MM-DD ddd"
                            autoFocus
                        />
                    </div>
                )}

                {/* Live preview */}
                <div className="dfd-row">
                    <span className="dfd-preview-label">Example</span>
                    <span className="dfd-preview">{isValid ? preview : '—'}</span>
                </div>

                {/* Format tokens reference */}
                <div className="dfd-info">
                    <table className="dfd-token-table">
                        <tbody>
                            <tr><td>YYYY</td><td>Full year</td><td className="dfd-ex">{moment().format('YYYY')}</td></tr>
                            <tr><td>YY</td><td>Short year</td><td className="dfd-ex">{moment().format('YY')}</td></tr>
                            <tr><td>MM</td><td>Month number</td><td className="dfd-ex">{moment().format('MM')}</td></tr>
                            <tr><td>MMM</td><td>Month short</td><td className="dfd-ex">{moment().format('MMM')}</td></tr>
                            <tr><td>MMMM</td><td>Month full</td><td className="dfd-ex">{moment().format('MMMM')}</td></tr>
                            <tr><td>DD</td><td>Day number</td><td className="dfd-ex">{moment().format('DD')}</td></tr>
                            <tr><td>ddd</td><td>Day short</td><td className="dfd-ex">{moment().format('ddd')}</td></tr>
                            <tr><td>dddd</td><td>Day full</td><td className="dfd-ex">{moment().format('dddd')}</td></tr>
                            <tr><td>HH</td><td>Hours 24h</td><td className="dfd-ex">{moment().format('HH')}</td></tr>
                            <tr><td>hh</td><td>Hours 12h</td><td className="dfd-ex">{moment().format('hh')}</td></tr>
                            <tr><td>mm</td><td>Minutes</td><td className="dfd-ex">{moment().format('mm')}</td></tr>
                            <tr><td>ss</td><td>Seconds</td><td className="dfd-ex">{moment().format('ss')}</td></tr>
                            <tr><td>A</td><td>AM / PM</td><td className="dfd-ex">{moment().format('A')}</td></tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="dfd-footer">
                <button className="dfd-btn dfd-btn-cancel" onClick={onClose}>Cancel</button>
                <button className={`dfd-btn dfd-btn-save${canSave ? ' ready' : ''}`} onClick={handleSave} disabled={!canSave}>
                    Save
                </button>
            </div>
        </div>,
        document.body
    );
}
